import type { FC, PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import classNames from "classnames";

import { isServerSide } from "@jti/fe-common";

import styles from "./StickyContainer.module.scss";

export type StickyContainerProps = {
  position: "top" | "bottom";
  portalDomNode?: HTMLElement;
};

export const StickyContainer: FC<PropsWithChildren<StickyContainerProps>> = ({ position, children, portalDomNode }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { height } = entry.contentRect;
          document.documentElement.style.setProperty(`--sticky-container-${position}-height`, `${height}px`);
        }
      });

      observer.observe(contentRef.current);

      return () => {
        if (contentRef.current) {
          observer.unobserve(contentRef.current);
        }
        observer.disconnect();
      };
    }
  }, []);

  if (isServerSide()) {
    return null;
  }

  return createPortal(
    <div className={classNames(styles.container, styles[position])}>
      <div className={styles.innerWrapper} ref={contentRef}>
        {children}
      </div>
    </div>,
    portalDomNode || document.body
  );
};
